import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { films } from "@/mocks/films";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterSidebar from "@/pages/category/components/FilterSidebar";

const PAGE_SIZE = 8;
const sortOptions = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "热门程度" },
  { key: "name", label: "名称" },
];

function matchYear(year: number, filter: string) {
  if (filter === "近5年") return year >= 2021;
  if (filter === "5年以上") return year < 2021;
  return true;
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get("type") || "全部";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const genre = searchParams.get("genre") || "全部";
  const sort = searchParams.get("sort") || "latest";
  const page = Number(searchParams.get("page") || "1") || 1;

  useSeo({
    title: "影视分类 - 顽皮的女朋友影视内容大全",
    description:
      "按类型、地区、年份与题材筛选顽皮的女朋友影视内容，浏览电影与电视剧分类下的影视作品资料与剧情介绍。",
    keywords: "影视分类,电影分类,电视剧分类,顽皮的女朋友,影视内容大全",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部" || (key === "sort" && value === "latest")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const filtered = useMemo(() => {
    const result = films.filter((film) => {
      if (type !== "全部" && film.type !== type) return false;
      if (region !== "全部" && film.region !== region) return false;
      if (genre !== "全部" && !film.genres.includes(genre)) return false;
      if (year !== "全部" && !matchYear(film.year, year)) return false;
      return true;
    });

    if (sort === "hot") result.sort((a, b) => b.heat - a.heat);
    else if (sort === "name") result.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    else result.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));

    return result;
  }, [type, region, genre, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeSummary = [type, region, year, genre]
    .filter((item) => item !== "全部")
    .join(" · ");

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视分类", to: "/category" },
          { label: activeSummary || "全部影视" },
        ]}
      />

      <header className="mt-5">
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">
          影视分类
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
          探索多种影视类型，挑选喜欢的影片。通过类型、地区、年份与题材多维筛选，快速定位想看的内容。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-8 items-start">
        <FilterSidebar
          type={type}
          region={region}
          year={year}
          genre={genre}
          onChange={updateParam}
          onReset={resetFilters}
        />

        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-background-200/70">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 部影视作品
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70 w-fit">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => updateParam("sort", option.key)}
                  className={
                    sort === option.key
                      ? "px-3.5 py-1.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm cursor-pointer whitespace-nowrap"
                      : "px-3.5 py-1.5 rounded-full text-foreground-700 text-sm hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {pageItems.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>

              <div className="mt-10">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={(nextPage) => {
                    const params = new URLSearchParams(searchParams);
                    if (nextPage <= 1) params.delete("page");
                    else params.set("page", String(nextPage));
                    setSearchParams(params);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="mt-6 py-16 flex flex-col items-center text-center rounded-lg bg-background-100 border border-background-200/70">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
                <i className="ri-file-search-line text-3xl"></i>
              </span>
              <h2 className="font-heading font-semibold text-lg text-foreground-950">
                没有找到匹配的影视作品
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-sm">
                试试调整筛选条件，或重置后浏览全部影视内容。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-sm tracking-wide hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}